/* Precision Carry: browser-only PDF artwork rendering. PDF bytes are not sent to a conversion service.
 * PDF.js 6.4.299, Apache-2.0: https://github.com/mozilla/pdf.js
 */
(() => {
  'use strict';
  if (window.pcPdfArtwork) return;
  const BASE = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@6.4.299/';
  const MAX_BYTES = 20 * 1024 * 1024;
  let libraryPromise = null;
  const deadline = (promise, milliseconds, message) => {
    let timer;
    return Promise.race([
      promise,
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(message)), milliseconds); }),
    ]).finally(() => clearTimeout(timer));
  };
  const library = () => {
    if (!libraryPromise) {
      libraryPromise = deadline(import(BASE + 'legacy/build/pdf.mjs'), 20000,
        'The PDF preview reader did not load. Check your connection and try again, or upload a PNG, JPG or SVG.')
        .then(pdfjs => {
          pdfjs.GlobalWorkerOptions.workerSrc = BASE + 'legacy/build/pdf.worker.mjs';
          return pdfjs;
        }).catch(error => { libraryPromise = null; throw error; });
    }
    return libraryPromise;
  };
  async function render(file) {
    if (!file || !file.size || file.size > MAX_BYTES) {
      throw new Error('Please upload a nonempty PDF no larger than 20 MB.');
    }
    let task = null;
    let rendering = null;
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const prefix = new TextDecoder('ascii').decode(bytes.subarray(0, Math.min(1024, bytes.length)));
      if (!prefix.includes('%PDF-')) throw new Error('This file is not a readable PDF. Please export it again or upload a PNG, JPG or SVG.');
      const pdfjs = await library();
      task = pdfjs.getDocument({
        data: bytes,
        stopAtErrors: true,
        isEvalSupported: false,
        cMapUrl: BASE + 'cmaps/',
        cMapPacked: true,
        standardFontDataUrl: BASE + 'standard_fonts/',
        wasmUrl: BASE + 'wasm/',
        useSystemFonts: false,
      });
      const pdf = await deadline(task.promise, 20000,
        'This PDF took too long to open. Please upload a simpler one-page PDF or a PNG, JPG or SVG.');
      if (pdf.numPages !== 1) {
        throw new Error('Please upload a one-page PDF containing only the artwork you want engraved. Export the desired page separately.');
      }
      const page = await pdf.getPage(1);
      const initial = page.getViewport({ scale: 1 });
      if (!(initial.width > 0 && initial.height > 0) || !Number.isFinite(initial.width + initial.height)) {
        throw new Error('This PDF has no usable artwork page. Please export it again or upload an image.');
      }
      const viewport = page.getViewport({ scale: 1800 / Math.max(initial.width, initial.height) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.ceil(viewport.width));
      canvas.height = Math.max(1, Math.ceil(viewport.height));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('Your browser could not create the PDF preview. Please try a current browser or upload an image.');
      rendering = page.render({ canvasContext: context, viewport, background: 'rgba(0,0,0,0)' });
      await deadline(rendering.promise, 25000,
        'This PDF took too long to render. Please upload a simpler one-page PDF or a PNG, JPG or SVG.');
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      let visible = false;
      for (let i = 3; i < pixels.length; i += 4) { if (pixels[i] > 16) { visible = true; break; } }
      if (!visible) throw new Error('This PDF has no visible artwork. Please upload a PDF or image with a visible design.');
      const blob = await new Promise((resolve, reject) => canvas.toBlob(value => value && value.size
        ? resolve(value) : reject(new Error('The PDF preview could not be saved. Please try again or upload an image.')), 'image/png'));
      return { blob, width: canvas.width, height: canvas.height, pageNumber: 1, pageCount: 1 };
    } catch (error) {
      if (error && error.name === 'PasswordException') {
        throw new Error('Password-protected PDFs cannot be previewed. Please export an unlocked one-page PDF or upload a PNG, JPG or SVG.');
      }
      if (error && /^(Please upload|This (file|PDF)|The PDF preview|Your browser)/.test(error.message || '')) throw error;
      throw new Error('We could not render this PDF. Please export an unlocked one-page PDF, or upload a PNG, JPG or SVG.');
    } finally {
      if (rendering) { try { rendering.cancel(); } catch (_) {} }
      if (task) { try { await task.destroy(); } catch (_) {} }
    }
  }
  window.pcPdfArtwork = Object.freeze({ render, version: '6.4.299' });
})();
